import { useState, useEffect } from 'react';
import { Modal, Input, Button, Form, Message, Loader, Table, IconButton, ButtonToolbar } from 'rsuite';
import { Trash } from '@rsuite/icons';
import { useAuth } from '../utils/useAuth';
import { getApiUrl, getAuthHeaders } from '../utils/apiConfig';

function SecretsDialog({ show, onClose }) {
  const auth = useAuth();
  const [secretNames, setSecretNames] = useState([]);
  const [newSecretName, setNewSecretName] = useState('');
  const [newSecretValue, setNewSecretValue] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [success, setSuccess] = useState(false);
  const [removing, setRemoving] = useState({});

  // Fetch secret names when dialog opens
  useEffect(() => {
    if (show && auth.hasAuth0 && auth.isAuthenticated) {
      fetchSecretNames();
    }
  }, [show]);

  const fetchSecretNames = async () => {
    setFetching(true);
    setError('');
    
    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      
      const response = await fetch(getApiUrl('/api/users/me/secrets'), {
        method: 'GET',
        headers: headers
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail?.message || data.detail || 'Failed to fetch secrets');
      }

      setSecretNames(data.secret_names || []);
    } catch (err) {
      console.error('Failed to fetch secret names:', err);
      setError(err.message || 'Failed to fetch secrets');
      setSecretNames([]);
    } finally {
      setFetching(false);
    }
  };

  const handleAddSecret = async () => {
    setError('');
    setSuccess(false);
    
    // Validate secret name
    if (!newSecretName || newSecretName.trim().length === 0) {
      setError('Secret name is required and cannot be empty');
      return;
    }

    // Validate secret name format (alphanumeric and underscores only)
    if (!/^[a-zA-Z0-9_]+$/.test(newSecretName.trim())) {
      setError('Secret name can only contain letters, numbers, and underscores');
      return;
    }

    // Validate secret value
    if (!newSecretValue || newSecretValue.trim().length === 0) {
      setError('Secret value is required and cannot be empty');
      return;
    }

    setLoading(true);

    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      
      const response = await fetch(getApiUrl('/api/users/me/secrets'), {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          name: newSecretName.trim(), 
          value: newSecretValue.trim() 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail?.message || data.detail || 'Failed to add secret');
      }

      setSuccess(true);
      setError('');
      setNewSecretName('');
      setNewSecretValue('');
      
      // Refresh secret names list
      await fetchSecretNames();
      
      // Clear success message after a short delay
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Secret add error:', err);
      setError(err.message || 'Failed to add secret. Please try again.');
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSecret = async (secretName) => {
    setError('');
    setRemoving({ ...removing, [secretName]: true });

    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      
      const response = await fetch(getApiUrl(`/api/users/me/secrets/${encodeURIComponent(secretName)}`), {
        method: 'DELETE',
        headers: headers
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail?.message || data.detail || 'Failed to remove secret');
      }

      // Refresh secret names list
      await fetchSecretNames();
    } catch (err) {
      console.error('Secret remove error:', err);
      setError(err.message || 'Failed to remove secret. Please try again.');
    } finally {
      setRemoving({ ...removing, [secretName]: false });
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      setSuccess(false);
      setNewSecretName('');
      setNewSecretValue('');
      setSecretNames([]);
      onClose();
    }
  };

  return (
    <Modal open={show} onClose={handleClose} size="md">
      <Modal.Header>
        <Modal.Title>Manage Secrets</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {fetching ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Loader size="md" content="Loading secrets..." />
          </div>
        ) : (
          <>
            {error && (
              <Message type="error" showIcon style={{ marginBottom: 20 }}>
                {error}
              </Message>
            )}

            {success && (
              <Message type="success" showIcon style={{ marginBottom: 20 }}>
                Secret added successfully!
              </Message>
            )}

            {/* Existing Secrets List */}
            {secretNames.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <h5 style={{ marginBottom: 10 }}>Existing Secrets ({secretNames.length})</h5>
                <Table
                  data={secretNames.map(name => ({ name }))}
                  autoHeight
                  rowHeight={50}
                  bordered
                  cellBordered
                >
                  <Table.Column flexGrow={1} align="left">
                    <Table.HeaderCell>Secret Name</Table.HeaderCell>
                    <Table.Cell dataKey="name" />
                  </Table.Column>
                  <Table.Column width={100} align="center">
                    <Table.HeaderCell>Actions</Table.HeaderCell>
                    <Table.Cell>
                      {(rowData) => (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                          <IconButton
                            icon={<Trash />}
                            color="red"
                            appearance="subtle"
                            size="sm"
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to remove secret "${rowData.name}"?`)) {
                                handleRemoveSecret(rowData.name);
                              }
                            }}
                            loading={removing[rowData.name]}
                          />
                        </div>
                      )}
                    </Table.Cell>
                  </Table.Column>
                </Table>
              </div>
            )}

            {/* Add New Secret Form */}
            <div style={{ borderTop: '1px solid #eee', paddingTop: 20 }}>
              <h5 style={{ marginBottom: 15 }}>Add New Secret</h5>
              <Form fluid onSubmit={handleAddSecret}>
                <Form.Group>
                  <Form.ControlLabel>Secret Name</Form.ControlLabel>
                  <Input
                    value={newSecretName}
                    onChange={(value) => setNewSecretName(value)}
                    placeholder="e.g., API_KEY, DATABASE_PASSWORD"
                    disabled={loading || success}
                  />
                  <Form.HelpText>
                    Letters, numbers, and underscores only. Will be available as an environment variable in Docker containers.
                  </Form.HelpText>
                </Form.Group>

                <Form.Group>
                  <Form.ControlLabel>Secret Value</Form.ControlLabel>
                  <Input
                    value={newSecretValue}
                    onChange={(value) => setNewSecretValue(value)}
                    placeholder="Enter secret value"
                    type="password"
                    disabled={loading || success}
                  />
                  <Form.HelpText>
                    Secret value will be stored securely and cannot be retrieved after saving.
                  </Form.HelpText>
                </Form.Group>

                <Form.Group>
                  <Button 
                    appearance="primary" 
                    type="submit" 
                    block 
                    loading={loading}
                    disabled={success}
                  >
                    {success ? 'Added!' : 'Add Secret'}
                  </Button>
                </Form.Group>
              </Form>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleClose} appearance="subtle">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default SecretsDialog;

